import { getJourneyCurrentConversionEventName } from 'modules/workflows/ducks';
import { FieldValues } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { ReactNode } from 'react';
import { StyledBoldText, StyledModalBody } from './SaveChangesModalBody.styled';
import { ITERABLE_PURCHASE_EVENT_NAME } from '../../RuleBuilder/constants';

export const SAVE_CHANGES_MODAL_BODY_TEST_ID = 'save-changes-modal-body';

type DirtyValuesType = Partial<
  Readonly<{
    [x: string]: any;
  }>
>;

//  reusable components
const ChangeMessage = ({ message }: { message: string | ReactNode }) => <span>{message}</span>;
const Journeys = () => <>journey&apos;s</>;

const ChangeJourneyConversion = ({ conversion }: { conversion: string }) => (
  <div>
    Are you sure you want to change this <Journeys /> conversion event to{' '}
    <StyledBoldText>{conversion}</StyledBoldText>?
  </div>
);
const OnlyConversionAdded = ({ conversion }: { conversion: string }) => (
  <ChangeJourneyConversion conversion={conversion} />
);

const GeneralConversionAddedMessage = ({ newConversion }: { newConversion: string }) => (
  <>
    This journey and its associated campaigns will start recording conversions for the event
    <StyledBoldText>{newConversion}</StyledBoldText>.
  </>
);

const GeneralConversionChangeToNewConversionMessage = ({
  previousConversion,
  newConversion,
}: {
  previousConversion: string;
  newConversion: string;
}) => (
  <>
    This journey and its associated campaigns will stop recording conversions for the event{' '}
    <StyledBoldText>{previousConversion}</StyledBoldText> and start recording conversions for the
    event <StyledBoldText>{newConversion}</StyledBoldText>.
  </>
);

const PreviousConversionChangedToNewConversionExitRulesMessage = ({
  previousConversion,
  newConversion,
}: {
  previousConversion: string;
  newConversion: string;
}) => {
  if (newConversion === ITERABLE_PURCHASE_EVENT_NAME) {
    return (
      <>
        Any conversion met exit rules associated with the{' '}
        <StyledBoldText>{previousConversion}</StyledBoldText> conversion event will be removed since
        the <StyledBoldText>{newConversion}</StyledBoldText> conversion event isn&apos;t currently
        supported in exit rules.
      </>
    );
  }
  return (
    <>
      Any exit rules associated with the <StyledBoldText>{previousConversion}</StyledBoldText>{' '}
      conversion event will be updated to <StyledBoldText>{newConversion}</StyledBoldText>.
    </>
  );
};

const PreviousConversionChangeToNewConversion = ({
  previousConversion,
  newConversion,
  otherFieldsChanged,
}: {
  previousConversion: string;
  newConversion: string;
  otherFieldsChanged: boolean;
}) => (
  <>
    {otherFieldsChanged ? null : (
      <ChangeMessage
        message={
          <>
            Are you sure you want to change this <Journeys /> conversion event from{' '}
            <StyledBoldText>{previousConversion}</StyledBoldText> to{' '}
            <StyledBoldText>{newConversion}</StyledBoldText>?
          </>
        }
      />
    )}
    <ChangeMessage
      message={GeneralConversionChangeToNewConversionMessage({
        previousConversion,
        newConversion,
      })}
    />
    <ChangeMessage
      message={PreviousConversionChangedToNewConversionExitRulesMessage({
        previousConversion,
        newConversion,
      })}
    />
  </>
);

const ConversionAndOtherFieldsChange = ({
  message,
  previousConversion,
  newConversion,
}: {
  message: any;
  previousConversion: string;
  newConversion: string;
}) => (
  <>
    <div>{message}</div>
    {previousConversion ? (
      <PreviousConversionChangeToNewConversion
        previousConversion={previousConversion}
        newConversion={newConversion}
        otherFieldsChanged={true}
      />
    ) : (
      <GeneralConversionAddedMessage newConversion={newConversion} />
    )}
  </>
);

// Conversion changes
const ConversionChanged = ({
  previousConversion,
  newConversion,
}: {
  previousConversion: string;
  newConversion: string;
}) => (
  <>
    {previousConversion ? (
      <PreviousConversionChangeToNewConversion
        previousConversion={previousConversion}
        newConversion={newConversion}
        otherFieldsChanged={false}
      />
    ) : (
      <OnlyConversionAdded conversion={newConversion} />
    )}
  </>
);

// everything
const EverythingHasChanged = ({
  previousConversion,
  newConversion,
}: {
  previousConversion: string;
  newConversion: string;
}) => (
  <ConversionAndOtherFieldsChange
    message={
      <>
        Are you sure you want to change this <Journeys /> conversion event, exit rules, metrics,
        name, and description?
      </>
    }
    previousConversion={previousConversion}
    newConversion={newConversion}
  />
);

const getModalBody = (
  formValues: FieldValues = {},
  dirtyValues: DirtyValuesType = {},
  currentConversion: string,
) => {
  const newConversion = (formValues?.conversions?.event as string) || '';

  const { about, metrics, conversions, exitRules } = dirtyValues || {};

  if (!about && !metrics && !exitRules && !!conversions)
    return (
      <ConversionChanged previousConversion={currentConversion} newConversion={newConversion} />
    );
  else if (!!about?.name && !!about?.description && !!metrics && !!conversions && !!exitRules)
    return (
      <EverythingHasChanged previousConversion={currentConversion} newConversion={newConversion} />
    );

  const ifl = new Intl.ListFormat('en');

  const keyMapper = {
    conversions: 'conversion event',
    exitRules: 'exit rules',
    labelIds: 'labels',
    visitorTrackingEnabled: 'exit event tracking',
  };

  const aboutFilter = Object.keys(dirtyValues?.about || {})
    .map((key) => {
      const valueFromKeyMapper = keyMapper[`${key as keyof typeof keyMapper}`] ?? key;
      return valueFromKeyMapper;
    }) // remap the key to a more user friendly display name
    .sort((a, b) => b.localeCompare(a)); // collect the keys from the about dirty values, sort them in reverse alphabetical order to match form order

  const dirtyValuesArray = Object.keys(dirtyValues)
    .filter((key) => key !== 'about') // remove about from the list
    .map((key) => {
      const valueFromKeyMapper = keyMapper[`${key as keyof typeof keyMapper}`] ?? key;
      return valueFromKeyMapper;
    }) // remap the key to a more user friendly display name
    .sort(); // sort the keys in alphabetical order to match form order.
  const dirtyList = [...dirtyValuesArray, ...aboutFilter];
  const listItems = dirtyList.length > 0 ? ifl.format(dirtyList) : 'settings';
  if (dirtyList.includes(keyMapper.conversions)) {
    return (
      <ConversionAndOtherFieldsChange
        message={`Are you sure you want to change this journey's ${listItems}?`}
        newConversion={newConversion}
        previousConversion={currentConversion}
      />
    );
  }
  return <ChangeMessage message={`Are you sure you want to change this journey's ${listItems}?`} />;
};

export const useSaveChangesModalBody = (formValues: FieldValues, dirtyValues: DirtyValuesType) => {
  const currentConversion = useSelector(getJourneyCurrentConversionEventName) || '';

  const ModalBody = getModalBody(formValues, dirtyValues, currentConversion);
  return <StyledModalBody data-test={SAVE_CHANGES_MODAL_BODY_TEST_ID}>{ModalBody}</StyledModalBody>;
};
